import React, { useEffect, useState } from 'react';

import { Ban, ChevronDown, ChevronRight, PlusCircle } from 'lucide-react';
import { useMutation, useQuery, queryCache } from 'react-query';
import { DotBricksLoading, ListPagination, Toast } from 'src/common/components';
import { useDebouncedValue } from 'src/common/hooks';

import { fetchInviteCodes, fetchRedemptions, revokeInviteCode } from '../api';
import { InviteCode, InviteCodeStatus, InviteRedemption, Paginated } from '../types';
import { codeStatusBadge, formatDate, formatDateTime, SOURCE_LABEL } from '../utils';

import { CopyableCode } from './CopyableCode';
import { GenerateCodeModal } from './GenerateCodeModal';

// Number of data columns after the leading chevron column (Code…Actions).
// Keep in sync with the <thead> below.
const DATA_COLUMN_COUNT = 9;

const STATUS_FILTERS: Array<{ key: string; label: string }> = [
  { key: '', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'exhausted', label: 'Exhausted' },
  { key: 'expired', label: 'Expired' },
  { key: 'revoked', label: 'Revoked' },
];

const StatusBadge: React.FC<{ status: InviteCodeStatus }> = ({ status }) => {
  const badge = codeStatusBadge(status);
  return (
    <span className={`inline-block text-xs px-2 py-0.5 rounded font-medium ${badge.classes}`}>
      {badge.label}
    </span>
  );
};

const inviteeLabel = (r: InviteRedemption): string => {
  if (!r.invitee) return 'Unknown patient';
  return r.invitee.name || r.invitee.email || r.invitee.phone || 'Unknown patient';
};

const RedemptionsDetail: React.FC<{ codeId: string }> = ({ codeId }) => {
  const { data, isLoading, isError } = useQuery<{ data: Paginated<InviteRedemption> }>(
    ['codeRedemptions', codeId],
    () => fetchRedemptions({ page: 1, invite_code: codeId }),
    { keepPreviousData: true }
  );

  const redemptions = data?.data?.results ?? [];
  const totalCount = data?.data?.count ?? 0;

  if (isLoading) return <span className="text-sm text-gray-400">Loading redemptions…</span>;
  if (isError) return <span className="text-sm text-red-600">Could not load redemptions.</span>;
  if (redemptions.length === 0)
    return <span className="text-sm text-gray-400">No redemptions yet.</span>;

  return (
    <div>
      <p className="mb-1.5 text-xs font-medium tracking-wider text-gray-500 uppercase">
        Redemptions ({totalCount})
      </p>
      <div className="space-y-1">
        {redemptions.map((r) => (
          <div key={r.id} className="gap-x-2 flex flex-wrap items-center text-sm">
            <span className="text-gray-900">{inviteeLabel(r)}</span>
            <span className="text-gray-400">· invited by {r.inviter_username || 'Staff'}</span>
            <span className="text-gray-400">· {formatDateTime(r.redeemed_at)}</span>
          </div>
        ))}
      </div>
      {totalCount > redemptions.length && (
        <p className="mt-1.5 text-xs text-gray-400">
          Showing first {redemptions.length} of {totalCount}.
        </p>
      )}
    </div>
  );
};

const CodeRow: React.FC<{ code: InviteCode }> = ({ code }) => {
  const [expanded, setExpanded] = useState(false);
  const hasRedemptions = code.used_count > 0;

  const [revoke, { isLoading: revoking }] = useMutation(() => revokeInviteCode(code.id), {
    onSuccess: () => {
      Toast.success(`Code ${code.code} revoked.`);
      queryCache.invalidateQueries('inviteCodes');
    },
    onError: () => {
      Toast.error('Could not revoke the code.');
    },
  });

  return (
    <>
      <tr className="hover:bg-gray-50 border-t border-gray-200">
        <td className="w-8 py-2 pl-3 pr-1 text-center">
          {hasRedemptions && (
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              aria-expanded={expanded}
              aria-label={`${expanded ? 'Collapse' : 'Expand'} redemptions for ${code.code}`}
              className="hover:text-gray-600 text-gray-400"
            >
              {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
            </button>
          )}
        </td>
        <td className="px-4 py-2 text-sm">
          <CopyableCode code={code.code} />
        </td>
        <td className="px-4 py-2 text-sm text-gray-600">{code.label || '—'}</td>
        <td className="px-4 py-2 text-sm">
          <StatusBadge status={code.status} />
        </td>
        <td className="px-4 py-2 text-sm text-gray-600">
          {code.used_count}
          <span className="text-gray-400"> / {code.max_uses}</span>
        </td>
        <td className="px-4 py-2 text-sm text-gray-600">{SOURCE_LABEL[code.source] || '—'}</td>
        <td className="px-4 py-2 text-sm text-gray-600">{code.campaign || '—'}</td>
        <td className="px-4 py-2 text-sm text-gray-600">{code.created_by_username || '—'}</td>
        <td className="px-4 py-2 text-sm text-gray-500">{formatDate(code.expires_at)}</td>
        <td className="px-4 py-2 text-sm text-right">
          {code.status !== 'revoked' && (
            <button
              type="button"
              onClick={() => {
                // Revoking is irreversible from this UI — confirm before mutating.
                // eslint-disable-next-line no-alert
                if (window.confirm(`Revoke code ${code.code}? This can't be undone.`)) revoke();
              }}
              disabled={revoking}
              className="hover:text-red-700 disabled:opacity-50 inline-flex items-center gap-1 text-xs text-red-600"
              aria-label="Revoke code"
            >
              <Ban size={13} />
              Revoke
            </button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-gray-50 border-t border-gray-100">
          <td className="w-8" />
          <td colSpan={DATA_COLUMN_COUNT} className="px-4 py-3">
            <RedemptionsDetail codeId={code.id} />
          </td>
        </tr>
      )}
    </>
  );
};

export const Codes: React.FC = () => {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [status, setStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const search = useDebouncedValue(searchInput);

  // Back to page 1 whenever the (debounced) search changes.
  useEffect(() => setPage(1), [search]);

  const { data, isLoading, isError } = useQuery<{ data: Paginated<InviteCode> }>(
    ['inviteCodes', page, search, status],
    () => fetchInviteCodes({ page, search, status }),
    { keepPreviousData: true }
  );

  const codes = data?.data?.results ?? [];
  const totalCount = data?.data?.count ?? 0;

  return (
    <div className="sm:px-6 lg:px-8 flex flex-col h-full pt-8 pb-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Invite codes</h2>
        <button
          type="button"
          onClick={() => setShowModal(true)}
          className="bg-primary-2.0 hover:bg-primary-3.0 inline-flex items-center gap-2 px-3 py-2 text-sm text-white rounded"
        >
          <PlusCircle size={16} />
          Generate codes
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key || 'all'}
            type="button"
            onClick={() => {
              setStatus(f.key);
              setPage(1);
            }}
            className={`px-3 py-1 rounded text-sm border transition-colors ${
              status === f.key
                ? 'bg-primary-2.0 text-white border-primary-2.0'
                : 'bg-white border-gray-300 hover:bg-gray-50'
            }`}
          >
            {f.label}
          </button>
        ))}
        <input
          type="search"
          placeholder="Search code, label, campaign…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="ml-2 border border-gray-300 rounded px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-2.0 w-64"
        />
      </div>

      {isLoading ? (
        <DotBricksLoading />
      ) : isError ? (
        <p className="text-red-600">Failed to load invite codes. Please try again.</p>
      ) : codes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <p className="text-base font-medium">No invite codes yet</p>
          <p className="mt-1 text-sm">Generate a code to get started.</p>
        </div>
      ) : (
        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-auto border border-gray-200 rounded shadow-sm">
            <table className="min-w-full text-left">
              <thead className="bg-gray-50 sticky top-0 text-xs tracking-wider text-gray-500 uppercase">
                <tr>
                  <th className="w-8 px-2 py-2" />
                  <th className="px-4 py-2">Code</th>
                  <th className="px-4 py-2">Label</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Uses</th>
                  <th className="px-4 py-2">Source</th>
                  <th className="px-4 py-2">Campaign</th>
                  <th className="px-4 py-2">Created by</th>
                  <th className="px-4 py-2">Expires</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {codes.map((code) => (
                  <CodeRow key={code.id} code={code} />
                ))}
              </tbody>
            </table>
          </div>
          <ListPagination totalCount={totalCount} currentPage={page} setCurrentPage={setPage} />
        </div>
      )}

      {showModal && <GenerateCodeModal onClose={() => setShowModal(false)} />}
    </div>
  );
};
